import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { COLOR_CONFLICT_FLAG, TOAST, CHARACTER_LENGTH } from "@constants/index";
import { faCopy, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutationData } from "@hooks/useMutationData";
import { formatDateTime, getLicensePolicyResult } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../../components/UI/Breadcrumb";
import { useTheme } from "@hooks/useTheme";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import PolicyConflictModal from "@components/UI/PolicyConflictModal";
import { PermissionContext } from "@contexts/PermissionContext";
import StatusBadge from "@components/UI/StatusBadge";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomTextarea } from "@components/Forms";
import { extractErrorCode } from "@utils/error";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";

const TEST_ID = "imported-component-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

const ImportedComponentDetail = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { importComponentSeq } = useParams();
  const { hasProjectManagementPermission } = useContext(PermissionContext);
  const { hasEditPermission } = useRoutePermissions();
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [importComponentData, setImportComponentData] = useState({});
  const [editingData, setEditingData] = useState({});
  const [globalPolicy, setGlobalPolicy] = useState({});
  const [policyConflictColor, setPolicyConflictColor] = useState("#ffffff");
  const [isScanning, setIsScanning] = useState(false);
  const [isStatusBlinking, setIsStatusBlinking] = useState(false);
  const [selectedLicensePolicyResult, setSelectedLicensePolicyResult] = useState(null);
  const [isPolicyConflictModalOpen, setIsPolicyConflictModalOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
 
  const navigate = useNavigate();

  // API
  const getImportComponentData = useMutationData(
    queryKeys.getImportComponentBySeq + "/" + importComponentSeq,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setImportComponentData(response);
          const licensePolicyResult = getLicensePolicyResult(globalPolicy, response.licenseAttribute);
          setPolicyConflictColor(licensePolicyResult.policyConflictColor);
          setIsInitialLoad(false);
        }
      },
      onError: (error) => {
        setIsInitialLoad(false);
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/imported-component-list" } });
        } else {
          ToastConstant(
            TOAST.error,
            t("toast.imported-components.get-imported-component-error-title"),
            error?.message || t("common.unknown-error"),
          );
        }
      },
    },
  );

  const licensePoliciesData = useMutationData(
    queryKeys.getListLicensePolicy,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (response !== "string") {
          const policy = response.find((policy) => policy.globalFlag === "Y");
          setGlobalPolicy(policy);
          if (policy && importComponentData?.licenseAttribute) {
            const licensePolicyResult = getLicensePolicyResult(policy, importComponentData.licenseAttribute);
            setPolicyConflictColor(licensePolicyResult.policyConflictColor);
          }
        }
      },
      onError: (error) => {
        ToastConstant(
          TOAST.error,
          t("toast.imported-components.get-license-policy-error-title"),
          error?.message || t("common.unknown-error"),
        );
      },
    },
  );

  const updateImportComponent = useMutationData(
    queryKeys.updateImportComponent,
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        // Update data from API response
        if (response && typeof response !== "string") {
          setImportComponentData(response);
        }
        setIsEditingMode(false);
        setEditingData({});
      },
      onError: (error) => {
        ToastConstant(
          TOAST.error,
          t("toast.imported-components.update-error-title"),
          error?.message || t("common.unknown-error"),
        );
      },
    },
  );

  const importComponentForDisabled = useMutationData(
    queryKeys.importComponentForDisabled,
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        setImportComponentData(response);
      },
      onError: (error) => {
        if ((error?.response?.data).includes("Agent block API failed!")) {
          ToastConstant(
            TOAST.error,
            t("toast.imported-components.disable-error-title"),
            t(error?.response?.data),
          );
        } else {
          ToastConstant(
            TOAST.error,
            t("toast.imported-components.disable-error-title"),
            error?.message || t("common.unknown-error"),
          );
        }
      },
    },
  );

  const importComponentForApprove = useMutationData(
    queryKeys.importComponentForApprove,
    {
      method: "PUT",
      skipErrorToast: true, // Skip default error toast, handle manually
    },
    {
      onSuccess: (response) => {
        setImportComponentData(response);
      },
      onError: (error) => {
        if (error?.response && error?.response?.data) {
          if ((error?.response?.data).includes("Agent allow API failed")) {
            ToastConstant(
              TOAST.error,
              t("project.notice-infor.approve-error"),
              t(error?.response?.data),
            );
          } else {
            ToastConstant(
              TOAST.error,
              t("project.notice-infor.approve-error", { error: error.message || "Unknown error" }),
              t("project.notice-infor.approve-error"),
            );
          }
        }
      },
    },
  );

  const importComponentForScan = useMutationData(
    queryKeys.importComponentForScan,
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        setImportComponentData(response.data);
        setIsScanning(true);
        setIsStatusBlinking(true);
      },
      onError: (error) => {
        console.error("Error during scan component:", error);
        const errorCode = extractErrorCode(error, "toast.imported-components.scan-error");
        ToastConstant(TOAST.error, t("toast.imported-components.scan-error-title"), t(errorCode));
        if (errorCode !== "errorcode.20500") { 
          setIsScanning(false);
          setIsStatusBlinking(false);
        } else {
          getImportComponentData.mutate();
        }
      },
    },
  );

  // Handler
  const handleEdit = () => {
    if (isEditingMode) {
      const payload = {
        ...importComponentData,
        ...editingData,
      };
      updateImportComponent.mutate(payload);
    } else {
      setEditingData({
        component: importComponentData?.component || "",
        componentVersion: importComponentData?.componentVersion || "",
        componentGroup: importComponentData?.componentGroup || "",
        importFileDownloadUrl: importComponentData?.importFileDownloadUrl || "",
        remark: importComponentData?.remark || "",
      });
      setIsEditingMode(true);
    }
  };

  const handleImportComponentForDisabled = async () => {
    const payload = { importComponentSeq };
    await importComponentForDisabled.mutateAsync(payload);

    // Updating new data after status changed
    getImportComponentData.mutate()
  };

  const handleImportComponentForApprove = async () => {
    const payload = { importComponentSeq };
    await importComponentForApprove.mutateAsync(payload);

    getImportComponentData.mutate();
  };

  const handleImportComponentForScan = () => {
    const payload = { importComponentSeq };
    importComponentForScan.mutate(payload);
  };

  const handleClose = () => {
    setIsEditingMode(false);
    setEditingData({});
  };

  const handleInputChange = (field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCopyUrl = async () => {
    if (!importComponentData?.importFileDownloadUrl) {
      ToastConstant(TOAST.error, t("toast.URL-copy-failed.title"), t("toast.no-URL-to-copy.message"));
      return;
    }

    try {
      // Check Clipboard API permission
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(importComponentData.importFileDownloadUrl);
        ToastConstant(
          TOAST.success,
          t("toast.URL-copied-to-clipboard.title"),
          t("toast.URL-copied-to-clipboard.message"),
        );
      } else {
        // Fallback if Clipboard API is not available
        fallbackCopyTextToClipboard(importComponentData.importFileDownloadUrl);
      }
    } catch (err) {
      console.error("Clipboard API failed:", err);
      // Try fallback if Clipboard API fails
      fallbackCopyTextToClipboard(importComponentData.importFileDownloadUrl);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      // Hide textarea safely
      textArea.style.position = "absolute";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        ToastConstant(
          TOAST.success,
          t("toast.URL-copied-to-clipboard.title"),
          t("toast.URL-copied-to-clipboard.message"),
        );
      } else {
        ToastConstant(TOAST.error, t("toast.URL-copy-failed.title"), t("toast.URL-copy-failed.message"));
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      ToastConstant(TOAST.error, t("toast.URL-copy-failed.title"), t("toast.URL-copy-failed.message"));
    }
  };

  const getStatusConfig = (statusCd) => {
    const statusConfig = {
      I: { color: "#01BBFE", text: "Imported" },
      D: { color: "#969696", text: "Disabled" },
      A: { color: "#00B649", text: "Approved" },
      O: { color: "#FDC101", text: "Scanning by Clarity " },
      S: { color: "#FF9501", text: "Scanned" },
      P: { color: "#C084FF", text: "Pending" },
    };
    return statusConfig[statusCd] || { color: "#9CA3AF", text: "Scan Failed" };
  };

  // Hook
  useEffect(() => {
    getImportComponentData.mutate();
    licensePoliciesData.mutate();
  }, []);

  useEffect(() => {
    let intervalId;
    if (importComponentData?.importComponentStatusCd === "O") {
      setIsStatusBlinking(true);
      setIsScanning(true);
      intervalId = setInterval(() => {
        getImportComponentData.mutate();
      }, 3000);
    }

    if (
      (importComponentData?.importComponentStatusCd === "S" ||
        (importComponentData?.importComponentStatusCd === "F")) &&
      isScanning
    ) {
      setIsScanning(false);
      setIsStatusBlinking(false);
      clearInterval(intervalId);
    }

    return () => clearInterval(intervalId);
  }, [isScanning, importComponentData?.importComponentStatusCd, getImportComponentData]);

  const columns = ["licensePolicyConflictFlag", "component", "componentVersion", "componentGroup", "componentLicense"];
  const columnsConfig = {
    licensePolicyConflictFlag: {
      headerRender: () => t("project.imported-components.license-conflict"),
      customRender: (value, row) => {
        if (row?.scannedFlag === null || row?.scannedFlag === undefined || row?.scannedFlag === "N") {
          return <div className={`text-sm center font-medium ${colors.textMessage}`}>---</div>;
        }

        const hasConflict = row?.licensePolicyConflictFlag === "Y";
        const backgroundColor = hasConflict ? COLOR_CONFLICT_FLAG.LICENSE_CONFLICT : COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT;
        return (
          <div className="flex items-center justify-center w-full" data-testid={tid("table-license-conflict")}>
            <div className="flex items-center gap-x-0.5" data-testid={tid("table-license-conflict-container")}>
              <div
                className={`w-8 h-8 rounded flex items-center justify-center text-base font-bold rounded-l-sm ${hasConflict ? "cursor-pointer" : ""}`}
                style={{ backgroundColor }}
                data-testid={tid("table-license-conflict-badge")}
                onClick={() => {
                  if (hasConflict) {
                    const licensePolicyResult = getLicensePolicyResult(globalPolicy, row?.licenseAttribute);
                    setSelectedLicensePolicyResult(licensePolicyResult);
                    setIsPolicyConflictModalOpen(true);
                  }
                }}
              >
                <span className={`${colors.textButton} ${hasConflict ? colors.textStaticWhite : colors.textConflictUndefined }`}>L</span>
              </div>
            </div>
          </div>
        );
      },
      attributeTitle: false,
      style: { width: "7%", textAlign: "center" },
    },
    component: {
      headerRender: () => t("project.imported-components.component-name"),
      customRender: (value, row) => (
        <div className={`text-sm font-medium ${colors.textMessage} truncate overflow-hidden text-ellipsis whitespace-nowrap`} title={row?.component || undefined}>
          {row?.component || "---"}
        </div>
      ),
    },
    componentVersion: {
      headerRender: () => t("project.imported-components.componentVersion"),
      customRender: (value, row) => (
        <div className={`text-sm font-medium ${colors.textMessage} truncate overflow-hidden text-ellipsis whitespace-nowrap`} title={row?.componentVersion || undefined}>
          {row?.componentVersion || "---"}
        </div>
      ),
    },
    componentGroup: {
      headerRender: () => t("project.imported-components.componentGroup"),
      customRender: (value, row) => (
        <div className={`text-sm font-medium ${colors.textMessage} truncate overflow-hidden text-ellipsis whitespace-nowrap`} title={row?.componentGroup || undefined}>
          {row?.componentGroup || "---"}
        </div>
      ),
    },
    componentLicense: {
      headerRender: () => t("project.imported-components.component-license"),
      customRender: (value, row) => (
        <div className={`text-sm font-medium ${colors.textMessage} truncate`} title={row?.componentLicense || undefined}>
          {row?.componentLicense || "---"}
        </div>
      ),
    },
  };

  const statusConfig = getStatusConfig(importComponentData?.importComponentStatusCd);

  if (isInitialLoad && (getImportComponentData.isPending || licensePoliciesData.isPending)) {
    return (
      <div data-testid={tid("loading")}>
        <LoadingSpinner data-testid={tid("loading-spinner")} />
      </div>
    );
  }

  return (
    <div className={`${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header */}
      <div className="mb-6" data-testid={tid("header")}>
        {/* Breadcrumb */}
        <Breadcrumb
          data-testid={tid("breadcrumb")}
          items={[
            {
              label: t("project.imported-components.import-title"),
              href: "/imported-component-list",
            },
            {
              label: t("project.imported-components.view-title"),
            },
          ]}
          className="mb-4"
        />

        {/* Title and Action Buttons */}
        <div className="flex items-center justify-between" data-testid={tid("title-row")}>
          <div className="flex-1 min-w-0 mr-4">
            <div className="flex items-center gap-3">
              <h1 className={`text-2xl truncate font-bold ${colors.textPrimary} break-words`} data-testid={tid("title")}>
                {importComponentData?.component}
              </h1>
            </div>
          </div>

          <div className="flex items-center space-x-3" data-testid={tid("header-actions")}>
            {!isEditingMode &&
              hasEditPermission && (
                <OutlineButton data-testid={tid("scan-button")} disabled={isScanning || importComponentForScan.isPending}  onClick={handleImportComponentForScan}>
                  <span>{t("common.scan-button")}</span> 
                  {importComponentForScan.isPending && (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" data-testid={tid("scan-button-spinner")} />
                  )}
                </OutlineButton>
              )}
            {(importComponentData?.allowFlag === null || importComponentData?.allowFlag === "N") &&
              hasProjectManagementPermission && (
                <PrimaryButton data-testid={tid("allow-button")} disabled={isScanning || importComponentForApprove.isPending} onClick={handleImportComponentForApprove} >
                  <span>{t("project.imported-components.allow")}</span>
                  {importComponentForApprove.isPending && (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" data-testid={tid("allow-button-spinner")} />
                  )}
                </PrimaryButton>
              )}

            {importComponentData?.allowFlag !== "N" &&
              !isEditingMode &&
              hasProjectManagementPermission && (
                <PrimaryButton data-testid={tid("block-button")} variant="danger" disabled={isScanning || importComponentForDisabled.isPending} onClick={handleImportComponentForDisabled} >
                  <span>{t("project.imported-components.block")}</span>
                  {importComponentForDisabled.isPending && (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" data-testid={tid("block-button-spinner")} />
                  )}
                </PrimaryButton>
              )}
          </div>
        </div>
      </div>

      {/* Main Information Card */}

      <div className={`rounded-lg border ${colors.borderPrimary} ${colors.bgModal} p-6`} data-testid={tid("imported-info-section")}>
        <div className="flex items-center justify-between mb-4" data-testid={tid("imported-info-header")}>
          <h2 className={`text-xl font-bold ${colors.textPrimary}`} data-testid={tid("imported-info-title")}>
            {t("project.imported-components.imported-information")}
          </h2>
        </div>

        <div className="grid grid-cols-5 gap-6" data-testid={tid("imported-info-grid")}>
          {/* Row 1 */}
          <div data-testid={fieldContainerTestId("creator")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("creator")}>
              {t("project.imported-components.creator")}
            </label>
            <div data-testid={fieldValueTestId("creator")}>
              <UserNameWithId name={importComponentData?.createrName} id={importComponentData?.createrId} layout="inline" />
            </div>
          </div>

          <div data-testid={fieldContainerTestId("creation-date")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("creation-date")}>
              {t("project.imported-components.creation-date")}
            </label>
            <div className={`text-sm truncate font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("creation-date")}>
              {formatDateTime(importComponentData?.createdAt) || "---"}
            </div>
          </div>

          <div data-testid={fieldContainerTestId("last-modified-by")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("last-modified-by")}>
              {t("common.last-modified-by")}
            </label>
            <div data-testid={fieldValueTestId("last-modified-by")}>
              <UserNameWithId name={importComponentData?.updaterName} id={importComponentData?.updaterId} layout="inline" />
            </div>
          </div>

          <div data-testid={fieldContainerTestId("last-modified-at")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("last-modified-at")}>
              {t("common.last-modified-date")}
            </label>
            <div className={`text-sm truncate font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("last-modified-at")}>
              {formatDateTime(importComponentData?.updatedAt) || "---"}
            </div>
          </div>

          <div data-testid={fieldContainerTestId("component-status")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("component-status")}>
              {t("project.imported-components.component-status")}
            </label>
            <div className={`flex items-center space-x-2 ${isStatusBlinking ? "status-blinking" : ""}`} data-testid={fieldValueTestId("component-status")}>
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: statusConfig.color }} data-testid={tid("component-status-dot")} />
              <div
                className={`text-sm truncate font-medium ${colors.textMessage}`}
                style={{ color: statusConfig.color }}
                data-testid={tid("component-status-text")}
              >
                {statusConfig.text}
              </div>
            </div>
          </div>

          <div className="col-span-5 -my-6" data-testid={tid("imported-info-divider-1")}>
            <div className={`border-t border-dashed ${colors.borderPrimary} w-full my-4`}></div>
          </div>

          {/* Row 3 */}
          <div className="-mt-4" data-testid={fieldContainerTestId("nexus-download-url")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("nexus-download-url")}>
              {t("project.imported-components.nexus-download-url")}
            </label>
            {isEditingMode ? (
              <input
                type="url"
                value={editingData.importFileDownloadUrl || ""}
                onChange={(e) => handleInputChange("importFileDownloadUrl", e.target.value)}
                className={`w-full rounded-lg border px-3 py-1 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                placeholder="Enter download URL"
                data-testid={fieldInputTestId("nexus-download-url")}
              />
            ) : (
              <div className="flex items-center space-x-2 h-8">
                <div className={`text-sm truncate font-medium ${colors.textMessage} min-h-8 py-2 break-words`} title={importComponentData?.importFileDownloadUrl || undefined} data-testid={fieldValueTestId("nexus-download-url")}>
                  {importComponentData?.importFileDownloadUrl || "---"}
                </div>
                {importComponentData?.importFileDownloadUrl && (
                  <button
                    type="button"
                    onClick={handleCopyUrl}
                    className={`${colors.textActive} ${colors.textHoverActive} transition-colors`}
                    data-testid={tid("copy-url-button")}
                  >
                    <FontAwesomeIcon icon={faCopy} className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="-mt-4" data-testid={fieldContainerTestId("real-file-name")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("real-file-name")}>
              {t("project.imported-components.real-file-name")}
            </label>
            <div className={`text-sm font-medium ${colors.textMessage} min-h-8 py-2 break-words`} title={importComponentData?.realFileName || undefined} data-testid={fieldValueTestId("real-file-name")}>
              {importComponentData?.realFileName || "---"}
            </div>
          </div>

          <div className="-mt-4" data-testid={fieldContainerTestId("last-scanned-by")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("last-scanned-by")}>
              {t("project.imported-components.last-scanned-by")}
            </label>
            <div className={`text-sm font-medium ${colors.textMessage} min-h-8 py-2 break-words`} data-testid={fieldValueTestId("last-scanned-by")}>
              {importComponentData?.lastScannedByName || "---"}
            </div>
          </div>

          <div className="-mt-4" data-testid={fieldContainerTestId("scanned-at")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("scanned-at")}>
              {t("project.imported-components.scanned-at")}
            </label>
            <div className={`text-sm font-medium ${colors.textMessage} min-h-8 py-2 break-words`} data-testid={fieldValueTestId("scanned-at")}>
              {formatDateTime(importComponentData?.scannedAt) != "-"
                ? formatDateTime(importComponentData?.scannedAt)
                : "---"}
            </div>
          </div>

          <div className="-mt-4" data-testid={fieldContainerTestId("nexus-status")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("nexus-status")}>
              {t("project.imported-components.nexus-status")}
            </label>
            <div className={`text-sm font-medium ${colors.textMessage} min-h-8 py-2 break-words`} data-testid={fieldValueTestId("nexus-status")}>
              {importComponentData?.allowFlag === null ? (
                <div className="text-xs">---</div>
              ) : (
                <StatusBadge isAllowed={importComponentData?.allowFlag === "Y"} t={t} />
              )}
            </div>
          </div>

          <div className="col-span-5 -my-6" data-testid={tid("imported-info-divider-2")}>
            <div className={`border-t border-dashed ${colors.borderPrimary} w-full my-4`}></div>
          </div>

          {/* Row 4 - Description (full width) */}
          <div className="col-span-5 -mt-4" data-testid={fieldContainerTestId("description")}>
            <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("description")}>
              {t("project.imported-components.description")}
            </label>
            {isEditingMode ? (
              <>
                <CustomTextarea
                  value={editingData.remark || ""}
                  onChange={(e) => handleInputChange("remark", e.target.value)}
                  rows={3}
                  placeholder={t("project.imported-components.write-description")}
                  maxLength={CHARACTER_LENGTH.COMMENT}
                  data-testid={fieldInputTestId("description")}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("description-char-count")}>
                  {(editingData.remark || "").length}/{CHARACTER_LENGTH.COMMENT}
                </p>
              </>
            ) : (
              <CustomTextarea
                rows={3}
                value={importComponentData?.remark || ""}
                title={importComponentData?.remark}
                readOnly={true}
                data-testid={fieldValueTestId("description")}
              />
            )}
          </div>
        </div>
      </div>

      <div className={`rounded-lg border ${colors.borderPrimary} ${colors.bgModal} p-6 mt-8`} data-testid={tid("component-info-section")}>
        <div className="flex items-center justify-between mb-4" data-testid={tid("component-info-header")}>
          <h2 className={`text-xl font-bold ${colors.textPrimary}`} data-testid={tid("component-info-title")}>
            {t("project.imported-components.component-information")}
          </h2>

          <div className="flex items-center space-x-2" data-testid={tid("component-info-actions")}>
            {isEditingMode && (
              <button
                type="button"
                data-testid={tid("cancel-button")}
                disabled={isScanning}
                onClick={handleClose}
                className={`px-4 py-2 h-10 text-base border font-semibold rounded-lg ${colors.textActive} transition-colors disabled:opacity-50`}
              >
                {t("project.imported-components.cancel")}
              </button>
            )}
          </div>
        </div>

        {/* Use TableWithPagination to render component information as a single-row table */}
        <div data-testid={tid("component-info-table-wrapper")}>
        <TableWithPagination
          columns={columns}
          data={[importComponentData || {}]}
          columnConfig={columnsConfig}
          tableStyle="default"
          showPagination={false}
          maxRowsPerPage={1}
          currentPage={1}
          setCurrentPage={() => { }}
          rowKey="importComponentSeq"
          colSize={12}
          dataTestId={tid("component-info-table")}
        />
        </div>
      </div>


      <PolicyConflictModal
        isOpen={isPolicyConflictModalOpen}
        onClose={() => {
          setIsPolicyConflictModalOpen(false);
          setSelectedLicensePolicyResult(null);
        }}
        licensePolicyResult={selectedLicensePolicyResult}
        data-testid={tid("policy-conflict-modal")}
      />
    </div>
  );
};

export default ImportedComponentDetail;
